import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  type User,
  type UserCredential,
  signInWithPopup,
} from '@angular/fire/auth';
import { Store } from '@ngrx/store';
import { type IdTokenResult, onIdTokenChanged } from 'firebase/auth';
import {
  BehaviorSubject,
  type Observable,
  catchError,
  filter,
  firstValueFrom,
  from,
  of,
  throwError,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import * as PlatformIdentityActions from '../../../+state/platform-identity/platform-identity.actions';
import type { PlatformAdminProfile } from '../../../+state/platform-identity/platform-identity.models';
import type { RootState } from '../../interfaces/root-state.interface';

@Injectable({ providedIn: 'root' })
export class PlatformAuthService {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly auth = inject(Auth);
  private readonly http = inject(HttpClient);
  private readonly ngZone = inject(NgZone);

  private initialAuthCheckComplete = new BehaviorSubject<boolean>(false);
  // Guards against re-entry: getIdTokenResult(true) re-triggers onIdTokenChanged.
  private isValidating = false;

  constructor() {
    // NOTE: onIdTokenChanged here is the RAW firebase/auth callback, which fires
    // OUTSIDE Angular's zone. Run the handler inside the zone so the resulting
    // store / router / query-param updates trigger change detection — otherwise a
    // failed validation (e.g. 403 for a non-allowlisted account) updates state
    // but the login spinner stays stuck until a manual refresh.
    onIdTokenChanged(this.auth, (user) => {
      void this.ngZone.run(() => this.handleIdTokenChange(user));
    });
  }

  private async handleIdTokenChange(user: User | null): Promise<void> {
    if (user) {
      if (!this.isValidating) {
        this.isValidating = true;
        try {
          // Initial token to authorize the validate call (store is empty here).
          const initial = (await user.getIdTokenResult()) as IdTokenResult;

          // Backend verifies the token, checks the platform_admin table, sets
          // the platformAdmin custom claim, and returns the admin profile.
          const profile = await firstValueFrom(this.validate(initial.token));

          // Force-refresh so the freshly-set custom claim is on the token.
          const refreshed = (await user.getIdTokenResult(true)) as IdTokenResult;

          this.dispatchAdmin(user, refreshed.token, profile);
        } catch (error) {
          const errorMessage = this.extractUserFriendlyError(error);
          this.store.dispatch(
            PlatformIdentityActions.validateFailed({ error: errorMessage })
          );
          await this.auth.signOut();
          this.store.dispatch(PlatformIdentityActions.setAdmin({ admin: null }));
        } finally {
          this.isValidating = false;
        }
      }
    } else {
      this.store.dispatch(PlatformIdentityActions.setAdmin({ admin: null }));
    }

    if (!this.initialAuthCheckComplete.value) {
      this.initialAuthCheckComplete.next(true);
    }
  }

  loginWithGoogle(): Observable<UserCredential> {
    // No `hd` hosted-domain hint: admins sign in from several domains (see
    // getAllowedAdminDomains), and the platform_admin table is the real gate.
    const provider = new GoogleAuthProvider();
    return from(signInWithPopup(this.auth, provider)).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Firebase Sign-In failed'))
      )
    );
  }

  logout(): Observable<void> {
    this.store.dispatch(PlatformIdentityActions.setAdmin({ admin: null }));
    return from(this.auth.signOut()).pipe(catchError(() => of(undefined)));
  }

  /**
   * Resolves once the first Firebase auth state has been observed. Used by the
   * app initializer + route guard so navigation waits for auth to settle.
   */
  async initializeAuthState(): Promise<void> {
    await firstValueFrom(
      this.initialAuthCheckComplete.pipe(filter((complete) => complete === true))
    );
  }

  private validate(token: string): Observable<PlatformAdminProfile> {
    const url = `${environment.urls.backendApi}/platform/auth/validate`;
    return this.http.post<PlatformAdminProfile>(
      url,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }

  private dispatchAdmin(
    user: User,
    token: string,
    profile: PlatformAdminProfile
  ): void {
    this.store.dispatch(
      PlatformIdentityActions.setAdmin({
        admin: {
          token,
          uid: user.uid,
          email: user.email,
          name: user.displayName,
          photoURL: user.photoURL,
          profile,
        },
      })
    );
  }

  private extractUserFriendlyError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 403) {
        return 'Access denied. Your account is not authorized for the Platform Admin console.';
      }
      if (error.status === 401) {
        return 'Authentication failed. Please sign in again.';
      }
      const backendMessage = (error.error as { message?: string } | null)
        ?.message;
      if (typeof backendMessage === 'string' && backendMessage.length > 0) {
        return backendMessage;
      }
    }
    return 'Authentication failed. Please try again or contact support.';
  }
}
